import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Tenants from "@/pages/Tenants";
import Vehicles from "@/pages/Vehicles";
import VehicleForm from "@/pages/VehicleForm";
import QRCodes from "@/pages/QRCodes";
import QRDetail from "@/pages/QRDetail";
import Billing from "@/pages/Billing";
import ScanLogs from "@/pages/ScanLogs";
import Suspended from "@/pages/Suspended";
import Settings from "@/pages/Settings";
import SuperAdmin from "@/pages/SuperAdmin";
import Stickers from "@/pages/Stickers";
import BulkImport from "@/pages/BulkImport";
import Onboard from "@/pages/Onboard";
import OnboardReview from "@/pages/OnboardReview";
import SetupFee from "@/pages/SetupFee";
import BusinessType from "@/pages/BusinessType";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PublicScan from "@/pages/PublicScan";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsOfService from "@/pages/legal/TermsOfService";
import RefundPolicy from "@/pages/legal/RefundPolicy";
import TutorialOverlay from "@/components/TutorialOverlay";

function DashShell({ children }) {
  return <Layout>{children}</Layout>;
}

function OnboardingGate({ children }) {
  // Redirect to onboarding only AFTER /api/properties has resolved AND the
  // user genuinely has no properties. `properties === null` means still loading
  // — we must not redirect during that window or freshly-logged-in PMs get
  // bounced to /onboarding before their property list arrives.
  const { user, properties } = useAuth();
  if (
    user && user !== false &&
    properties !== null &&
    properties.length === 0 &&
    user.role !== "super_admin"
  ) {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

function SetupFeeGate({ children }) {
  // Property managers must settle the $99 setup fee before they can
  // create tenants/vehicles or activate QRs. The Dashboard itself is
  // intentionally NOT gated — managers can view their account state,
  // notifications, and pending invoice while waiting for payment to
  // clear. Backend endpoints (POST /tenants, POST /vehicles, QR
  // activate) return 402 with a clear message if a write is attempted
  // pre-payment.
  const { user, activeProperty } = useAuth();
  if (
    user && user !== false &&
    user.role !== "super_admin" &&
    activeProperty &&
    !activeProperty.is_demo &&
    !activeProperty.setup_fee_paid_at &&
    !activeProperty.invoice_paid_at
  ) {
    return <Navigate to="/onboarding/setup-fee" replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <TutorialOverlay />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/s/:code" element={<PublicScan />} />

            <Route path="/legal/privacy" element={<PrivacyPolicy />} />
            <Route path="/legal/terms" element={<TermsOfService />} />
            <Route path="/legal/refund" element={<RefundPolicy />} />

            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <Onboarding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/business-type"
              element={
                <ProtectedRoute>
                  <BusinessType />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/setup-fee"
              element={
                <ProtectedRoute>
                  <SetupFee />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <OnboardingGate>
                    <DashShell>
                      <Dashboard />
                    </DashShell>
                  </OnboardingGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/tenants"
              element={
                <ProtectedRoute>
                  <SetupFeeGate>
                    <DashShell>
                      <Tenants />
                    </DashShell>
                  </SetupFeeGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/vehicles"
              element={
                <ProtectedRoute>
                  <SetupFeeGate>
                    <DashShell>
                      <Vehicles />
                    </DashShell>
                  </SetupFeeGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/vehicles/new"
              element={
                <ProtectedRoute>
                  <SetupFeeGate>
                    <DashShell>
                      <VehicleForm />
                    </DashShell>
                  </SetupFeeGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/bulk-import"
              element={
                <ProtectedRoute>
                  <SetupFeeGate>
                    <DashShell>
                      <BulkImport />
                    </DashShell>
                  </SetupFeeGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/onboard"
              element={
                <ProtectedRoute>
                  <SetupFeeGate>
                    <DashShell>
                      <Onboard />
                    </DashShell>
                  </SetupFeeGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/onboard/review"
              element={
                <ProtectedRoute>
                  <SetupFeeGate>
                    <DashShell>
                      <OnboardReview />
                    </DashShell>
                  </SetupFeeGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/qr-codes"
              element={
                <ProtectedRoute>
                  <SetupFeeGate>
                    <DashShell>
                      <QRCodes />
                    </DashShell>
                  </SetupFeeGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/qr-codes/:vid"
              element={
                <ProtectedRoute>
                  <SetupFeeGate>
                    <DashShell>
                      <QRDetail />
                    </DashShell>
                  </SetupFeeGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/billing"
              element={
                <ProtectedRoute>
                  <DashShell>
                    <Billing />
                  </DashShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/stickers"
              element={
                <ProtectedRoute>
                  <DashShell>
                    <Stickers />
                  </DashShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/scan-logs"
              element={
                <ProtectedRoute>
                  <DashShell>
                    <ScanLogs />
                  </DashShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/suspended"
              element={
                <ProtectedRoute>
                  <DashShell>
                    <Suspended />
                  </DashShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/settings"
              element={
                <ProtectedRoute>
                  <DashShell>
                    <Settings />
                  </DashShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute roles={["super_admin"]}>
                  <DashShell>
                    <SuperAdmin />
                  </DashShell>
                </ProtectedRoute>
              }
            />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
